import useRequest from "@/lib/request/useRequest";
import { useState, useCallback } from "react";
import {
    useDisclosure,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    Button,
    Checkbox,
    Chip,
} from "@heroui/react";
import { faBookmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    ApplicationPlan,
    PlantingPlan,
    SimulatedCropPlan,
} from "@/types/cropPlan";
import dayjs from "dayjs";

export function ConfirmPrimaryPlan({
    fields,
    plantingPlan,
    applicationPlan,
    cropPlans,
    onSave,
    buttonSize,
}: {
    fields: { id: number; active_planting_operation: { id: number } | null }[];
    plantingPlan: PlantingPlan;
    applicationPlan?: ApplicationPlan;
    cropPlans?: SimulatedCropPlan[];
    onSave?: () => void;
    buttonSize?: "sm" | "md" | "lg";
}) {
    const existingPlantingOperationIds = fields
        .map((field) => field.active_planting_operation?.id)
        .filter((id): id is number => id !== undefined);

    const { useMutateRequest } = useRequest();

    const { isOpen, onOpen, onOpenChange } = useDisclosure();

    const [confirmOverwritePlanting, setConfirmOverwritePlanting] =
        useState<boolean>(true);

    const [saveOperations, setSaveOperations] = useState<boolean>(true);

    const savePlantingOperationMutation = useMutateRequest({
        url: `/planting_operations/bulk_upsert/`,
        method: "put",
        successMessage: "Planting Operations Saved",
        onSuccess: () => {
            setConfirmOverwritePlanting(false);
        },
    });

    const handleSavePlantingOperation = useCallback(() => {
        if (!plantingPlan || !confirmOverwritePlanting) {
            return;
        }
        savePlantingOperationMutation.mutate({
            data: fields.map((field) => {
                return {
                    id: field.active_planting_operation
                        && field.active_planting_operation.id !== -1
                        ? field.active_planting_operation.id
                        : null,
                    field: field.id,
                    start_date: dayjs(plantingPlan.date).format("YYYY-MM-DD"),
                    end_date: dayjs(plantingPlan.date).format("YYYY-MM-DD"),
                    crop_configuration: plantingPlan.crop_configuration,
                    varieties: plantingPlan.varieties,
                    field_rate: {
                        rate_1: plantingPlan.seeding_rate || 0,
                        rate_2: plantingPlan.seeding_depth || null,
                    },
                };
            }),
        });
    }, [fields, plantingPlan, savePlantingOperationMutation, confirmOverwritePlanting]);

    const savePrimaryCropPlanMutation = useMutateRequest<SimulatedCropPlan>({
        url: `/simulated_crop_plans/bulk_upsert/`,
        method: "put",
        successMessage: "Crop Plan Saved",
        onSuccess: onSave,
    });

    const savePrimaryCropPlan = useCallback(() => {
        savePrimaryCropPlanMutation.mutate({
            data: fields.map((field) => {
                const cropPlan = cropPlans?.find(
                    (plan) => plan.field === field.id
                        && plan.planting_plan?.id === plantingPlan.id
                        && plan.application_plan?.id === (applicationPlan?.id || null)
                );
                return {
                    id: cropPlan ? cropPlan.id : null,
                    is_primary: true,
                    field: field.id,
                    planting_plan: plantingPlan,
                    application_plan: applicationPlan,
                    save_operations: saveOperations,
                };
            }),
        });
    }, [
        fields,
        plantingPlan,
        applicationPlan,
        saveOperations,
        savePrimaryCropPlanMutation,
        cropPlans,
    ]);

    return (
        <>
            <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
                <ModalContent>
                    <ModalHeader>Confirm Nitrogen Plan</ModalHeader>
                    <ModalBody>
                        <div className="text-sm text-gray-500 mb-4">
                            Operation records are used for tracking plot
                            establishment and crop modeling. They can be managed
                            on the Operations tab of the Field Dashboard.
                        </div>
                        <Checkbox
                            isSelected={confirmOverwritePlanting}
                            onValueChange={setConfirmOverwritePlanting}
                        >
                            Log Planting Operation
                        </Checkbox>
                        {existingPlantingOperationIds.length > 0 &&
                            confirmOverwritePlanting && (
                                <Chip color="warning" variant="flat">
                                    <span className="font-bold">Warning:</span>{" "}
                                    Existing planting operation will be
                                    overwritten.
                                </Chip>
                            )}
                        <Checkbox
                            isSelected={saveOperations}
                            onValueChange={setSaveOperations}
                        >
                            Log Pending Application Operations
                        </Checkbox>
                        {saveOperations && (
                            <Chip color="warning" variant="flat">
                                <span className="font-bold">Warning:</span>{" "}
                                Existing pending application operations will be
                                overwritten.
                            </Chip>
                        )}
                        <Button
                            startContent={
                                <FontAwesomeIcon
                                    icon={faBookmark}
                                    className="mr-1"
                                />
                            }
                            color="primary"
                            isLoading={
                                savePrimaryCropPlanMutation.isPending ||
                                savePlantingOperationMutation.isPending
                            }
                            onPress={() => {
                                handleSavePlantingOperation();
                                savePrimaryCropPlan();
                            }}
                        >
                            Save Nitrogen Plan
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Button
                startContent={
                    <FontAwesomeIcon icon={faBookmark} className="mr-1" />
                }
                color="primary"
                size={buttonSize || "md"}
                onPress={onOpen}
                isDisabled={!plantingPlan}
            >
                Set Nitrogen Plan
            </Button>
        </>
    );
}
